import constants from "./constants";
import { Chess } from "chess.js";

const getLines = (move) => {
  // Handle case when move is not a string
  if (typeof move !== 'string') {
    console.warn('getLines received non-string move:', move);
    return [String(move)]; // Convert to string and return as array with single element
  }
  return move.split('|');
};

const destructureLine = (line) => {
  // Safety check for undefined or non-string line
  if (!line || typeof line !== 'string') {
    return [undefined, undefined];
  }
  
  try {
    let variationNum, movesInThisLine;
    
    if (line.includes(':')) {
      [variationNum, movesInThisLine] = line.split(':');
    } else {
      movesInThisLine = line;
      variationNum = undefined;
    }
    
    return [variationNum, movesInThisLine];
  } catch (error) {
    console.error("Error in destructureLine:", error);
    return [undefined, undefined];
  }
};

// Add reset cache function
const resetPositionCache = () => {
  if (getCurrentBoardPosition.positionCache) {
    
    getCurrentBoardPosition.positionCache = {};
  }
  
  // Also clear any global caches that might be used
  try {
    if (window.getCurrentBoardPosition && 
        window.getCurrentBoardPosition.positionCache) {
      window.getCurrentBoardPosition.positionCache = {};
    }
  } catch (e) {
    // Ignore errors in window access
  }
};

const getCurrentBoardPosition = (game, index) => {
  const history = game?.history({ verbose: true });
  // Check for empty state first
  if (!history || history.length === 0) {
    
    return constants.CHESSBOARD.START_POSITION;
  }

  // Base case: starting position
  if (index === '-1') {
    
    return history[0]?.before || game.fen() || constants.CHESSBOARD.START_POSITION;
  }

  // Use a static cache to avoid recalculating positions
  if (!getCurrentBoardPosition.positionCache) {
    getCurrentBoardPosition.positionCache = {};
  }

  // Check if we already calculated this position
  if (getCurrentBoardPosition.positionCache[index]) {
    
    return getCurrentBoardPosition.positionCache[index];
  }

  const isVariationMove = index.includes('|');
  

  // CRITICAL FIX: For variation moves, we need to handle the position calculation differently
  if (isVariationMove) {
    // For variation moves, we need to:
    // 1. Parse the move index to get main move and variation move
    // 2. Get the correct move details from the 'moves' array
    // 3. Use the game object to manually replay moves to get the position
    
    try {
      // Create a new chess instance to rebuild the position
      const tempGame = new Chess();
      
      // Parse the variation index - example: "2|0:1" means main move 2, variation 0, move 1 in variation
      const parts = index.split('|');
      if (parts.length === 2) {
        const parentMoveIdx = parseInt(parts[0]);
        const variationPart = parts[1].split(':');
        const variationIdx = parseInt(variationPart[0]);
        const moveInVariationIdx = parseInt(variationPart[1]);
        
        // console.log('[CHESS-FEN] Parsed variation move:', {
        //   parentMoveIdx,
        //   variationIdx,
        //   moveInVariationIdx
        // });
        
        // Replay main line moves up to the parent move
        for (let i = 0; i <= parentMoveIdx && i < history.length; i++) {
          if (history[i] && history[i].san) {
            tempGame.move(history[i].san);
          }
        }
        
        // Now get the variation moves from our internal representation
        // This requires access to the moves array which we don't have in this function
        // So instead, we'll use a more direct approach for known patterns
        
        // IMPORTANT: Get the correct position by precisely calculating the move index
        // For "2|0:0" (first move in variation from move 2)
        // We need history[3].after (since history starts with move 0)
        // For "2|0:1" (second move in variation from move 2)
        // We need history[4].after 
        
        // Calculate the correct history index for most common variation patterns
        let historyIndex = parentMoveIdx + moveInVariationIdx + 1;
        
        // Ensure index is within bounds
        historyIndex = Math.min(historyIndex, history.length - 1);
        historyIndex = Math.max(historyIndex, 0);
        
        // Get the position AFTER the move is made
        const position = history[historyIndex]?.after;
        
        // console.log('[CHESS-FEN] Variation move position calculation:', {
        //   parentMoveIdx,
        //   variationIdx,
        //   moveInVariationIdx,
        //   historyIndex,
        //   positionFound: !!position
        // });
        
        // Cache and return the position
        getCurrentBoardPosition.positionCache[index] = position;
        return position;
      }
    } catch (error) {
      console.error('[CHESS-FEN] Error calculating variation position:', error);
      // Fall back to normal calculation on error
    }
  }
  
  // Standard calculation for main line moves
  let moveNum = isVariationMove ? 0 : parseInt(index);
  
  // For main line moves, ensure moveNum is within bounds
  moveNum = Math.min(moveNum, history.length - 1);
  moveNum = Math.max(moveNum, 0);
  
  
  
  // This is the crucial part - get position AFTER the move is made
  const position = history[moveNum]?.after;
  
  // Cache the result for future calls
  getCurrentBoardPosition.positionCache[index] = position;
  
  return position;
}

const getMove = (moves, moveIdx) => {
  // Handle starting position variations - these use a different data structure
  if (moveIdx && moveIdx.startsWith('-1|')) {
    // For starting position variations, we can't use the old format
    // Return false to indicate this is not accessible via the old format
    return false;
  }
  
  const lines = getLines(moveIdx);

  if (!moves) {
    return;
  }

  let nextMove;
  let moveNum = 0;
  for (let line of lines) {
    const [variationNum, movesInThisLine] = destructureLine(line);

    if (variationNum) {
      if (!nextMove || !nextMove.variations) {
        console.warn('getMove: nextMove or variations is undefined', { nextMove, variationNum });
        return false;
      }
      
      moves = nextMove.variations[variationNum];
      
      if (!moves) {
        console.warn('getMove: variation not found', { variationNum, availableVariations: nextMove.variations.length });
        return false;
      }
    }

    nextMove = moves?.[movesInThisLine];
    moveNum = moveNum + 1 + parseInt(movesInThisLine);
  }

  if (nextMove) {
    nextMove['moveNum'] = moveNum;
    return nextMove;
  }

  return false;
};

const getCurrentVariation = (moves, moveIdx) => {
  // Handle starting position variations - these use a different data structure
  if (moveIdx && moveIdx.startsWith('-1|')) {
    // For starting position variations, we can't use the old format
    // Return null to indicate this is not accessible via the old format
    return null;
  }
  
  const lines = getLines(moveIdx);

  let nextMove;
  for (let line of lines) {
    const [variationNum, movesInThisLine] = destructureLine(line);

    if (variationNum) {
      if (!nextMove || !nextMove.variations) {
        console.warn('getCurrentVariation: nextMove or variations is undefined', { nextMove, variationNum });
        return null;
      }
      
      moves = nextMove.variations[variationNum];
      
      if (!moves) {
        console.warn('getCurrentVariation: variation not found', { variationNum, availableVariations: nextMove.variations.length });
        return null;
      }
    }

    nextMove = moves?.[movesInThisLine];
  }

  return moves;
}

const parseMove = (move) => {
  // Handle non-string moves
  if (typeof move !== 'string') {
    move = String(move);
  }

  const lastVariationOccurrenceIndex = move.lastIndexOf(':');

  // If no variation delimiter found, just use the whole move as currMoveNum
  if (lastVariationOccurrenceIndex === -1) {
    return {
      prefix: '',
      currMoveNum: parseInt(move) || 0,
    };
  }

  const prefix = move.substring(0, lastVariationOccurrenceIndex + 1);
  const currMoveNum = parseInt(move.substring(lastVariationOccurrenceIndex + 1)) || 0;

  return {
    prefix,
    currMoveNum,
  }
}

const isMoveValid = (newMove, moves) => {
  // If moves is empty or undefined, no move is valid
  if (!moves || moves.length === 0) {
    return false;
  }

  try {
    const lines = getLines(newMove);

    let nextMove;
    for (let line of lines) {
      const [variationNum, movesInThisLine] = destructureLine(line);

      if (variationNum) {
        // If we can't access variations, the move is invalid
        if (!nextMove || !nextMove.variations) {
          return false;
        }
        moves = nextMove.variations[variationNum];
        // If the variation doesn't exist, the move is invalid
        if (!moves) {
          return false;
        }
      }

      // Convert to number and check if it's a valid index
      const moveIndex = parseInt(movesInThisLine);
      if (isNaN(moveIndex) || moveIndex < 0 || moveIndex >= moves.length) {
        return false;
      }

      nextMove = moves[moveIndex];
    }

    return true;
  } catch (error) {
    console.error("Error in isMoveValid:", error);
    return false;
  }
};

const getPrevMoveNotation = (currentMoveNotation) => {
  if (currentMoveNotation === '') {
    return '-1';
  }

  const { prefix, currMoveNum } = parseMove(currentMoveNotation);
  const prevMoveNum = currMoveNum - 1;

  let prevMove;
  if (prevMoveNum > -1) {
    prevMove = `${prefix}${prevMoveNum}`;
  } else {
    prevMove = currentMoveNotation.substring(0, currentMoveNotation.lastIndexOf('|'));
  }

  return prevMove;
};

const createMove = (san, type) => {
  return {
    notation: san,
    content: '',
    arrows: [],
    ...(type && {
      creator: {
        id: -1,
        type
      }
    })
  };
};

const updateCommentIdInMoves = (moves, commentId) => {
  const updatedMoves = JSON.parse(JSON.stringify(moves));
  updatedMoves.forEach((move) => {
    if (move?.creator?.type === constants.CHESSBOARD.MOVE_TYPES.COMMENT && move.creator.id === -1) {
      move.creator.id = commentId;
    }

    if (move.variations && move.variations.length !== 0) {
      move.variations.forEach((variation, index) => {
        move.variations[index] = updateCommentIdInMoves(variation, commentId);
      });
    }
  });
  return updatedMoves;
};

const getFlippedBoardOrientation = (currentOrientation) => {
  if (currentOrientation === "white") return "black";
  if (currentOrientation === "black") return "white";
}

const onNextMove = (currentMove, setCurrentMove, moves) => {
  try {
    const { prefix, currMoveNum } = parseMove(currentMove);

    if (!isMoveValid(`${prefix}${currMoveNum + 1}`, moves)) {
      return;
    }

    setCurrentMove(`${prefix}${currMoveNum + 1}`);
  } catch (err) {
    console.error(err);

  }
};

const onPrevMove = (currentMove, setCurrentMove) => {
  try {
    const prevMove = getPrevMoveNotation(currentMove);
    setCurrentMove(prevMove);
  } catch (err) {
    console.error(err);
  }
};

const getLastMove = (moves) => {
  if (!moves) {
    return -1;
  }

  return moves.length - 1;
};

const getTurnInFen = (fen) => {
  const parts = fen.split(' ');

  return parts[1];
};

// Function to detect if a move represents a variation from the original game
// POTENTIALLY REDUNDANT, added to generate comments.
export const isVariationMove = (originalMoves, currentMoves, moveIndex) => {
  if (!originalMoves || !currentMoves || moveIndex < 0) {
    return false;
  }

  // If we're past the end of original moves, it's a variation
  if (moveIndex >= originalMoves.length) {
    return true;
  }

  // If there are any variations, we're in a variation state
  if (currentMoves.some(move => move.variations && move.variations.length > 0)) {
    return true;
  }

  // Check if the current move's notation matches the original at the same position
  if (currentMoves[moveIndex] &&
    originalMoves[moveIndex] &&
    currentMoves[moveIndex].notation !== originalMoves[moveIndex].notation) {
    return true;
  }

  // Check if the move has a creator property indicating it's a comment move
  if (currentMoves[moveIndex] &&
    currentMoves[moveIndex].creator &&
    currentMoves[moveIndex].creator.type === 'COMMENT') {
    return true;
  }

  return false;
};


const getMoveAndVariationIndices = (currentMoveIdx, moves) => {
  // Handle starting position variations - these don't have a valid reference in the old format
  if (currentMoveIdx && currentMoveIdx.startsWith('-1|')) {
    // For starting position variations, we can't provide a valid moveRef using the old format
    // Return undefined to indicate this is not a regular variation
    return [undefined, undefined];
  }
  
  const lines = getLines(currentMoveIdx);

  if (!moves) {
    return [undefined, undefined];
  }

  let nextMove;
  let moveNum = 0;
  let lastVariationIdx = undefined;
  let prevMoveRef = undefined;
  for (let line of lines) {
    const [variationNum, movesInThisLine] = destructureLine(line);

    if (variationNum) {
      if (!nextMove || !nextMove.variations) {
        console.warn('getMoveAndVariationIndices: nextMove or variations is undefined', { nextMove, variationNum });
        return [undefined, undefined];
      }
      
      moves = nextMove.variations[variationNum];
      
      if (!moves) {
        console.warn('getMoveAndVariationIndices: variation not found', { variationNum, availableVariations: nextMove.variations.length });
        return [undefined, undefined];
      }

      prevMoveRef = nextMove;
      lastVariationIdx = variationNum;
    }

    nextMove = moves?.[movesInThisLine];
    moveNum = moveNum + 1 + parseInt(movesInThisLine);
  }

  return [prevMoveRef, lastVariationIdx];
}

const isMoveInVariation = (moveIdx, moves) => {
  const lines = getLines(moveIdx);

  if (lines.length > 1) {
    return true;
  }

  return false;
}

export {
  getLines,
  destructureLine,
  getCurrentBoardPosition,
  getMove,
  parseMove,
  isMoveValid,
  getPrevMoveNotation,
  getCurrentVariation,
  createMove,
  updateCommentIdInMoves,
  getFlippedBoardOrientation,
  onNextMove,
  onPrevMove,
  getLastMove,
  getTurnInFen,
  getMoveAndVariationIndices,
  isMoveInVariation,
  resetPositionCache,
};